import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified API root
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:5000"; // optional for future deployment

const AdminSupportChat = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Single socket instance
  const socket = io(SOCKET_URL, { transports: ["websocket"], reconnection: true });

  // 🔹 Register admin socket listener
  useEffect(() => {
    socket.emit("registerSupportAdmin", "admin");
    socket.on("support:message", (msg) => {
      if (msg.email === activeSession?.email) {
        setMessages((prev) => [...prev, msg]);
      }
    });
    return () => socket.disconnect();
  }, [activeSession]);

  // 🔹 Load all active chat sessions
  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API_BASE}/support/chat-session-list`);
      setSessions(res.data || []);
    } catch (err) {
      console.error("❌ Failed to load sessions:", err);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  // 🔹 Open selected chat session
  const openSession = async (session) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/support/livechat/${session.email}`);
      setActiveSession(session);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("❌ Failed to open session:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Send message as Admin
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeSession) return;

    const msg = {
      sender: "Admin",
      email: activeSession.email,
      text: newMsg.trim(),
      time: new Date().toISOString(),
    };

    socket.emit("support:message", msg);
    setMessages((prev) => [...prev, msg]);
    setNewMsg("");

    try {
      await axios.post(`${API_BASE}/support/livechat/send`, msg);
    } catch (err) {
      console.error("❌ Failed to send message:", err);
    }
  };

  // 🔹 End Chat Session
  const endChat = async (email) => {
    try {
      socket.emit("support:endSession", { email, closedBy: "Admin" });
      await axios.post(`${API_BASE}/support/end-session`, {
        email,
        closedByAdmin: true,
      });
      setActiveSession(null);
      setMessages([]);
      loadSessions();
    } catch (err) {
      console.error("❌ Failed to end chat:", err);
    }
  };

  return (
    <div className="ml-64 p-6 min-h-screen bg-neutral-50">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
        💬 Live Support Chat
      </h1>

      <div className="grid grid-cols-4 gap-6">
        {/* ================= LEFT: Customer Info Panel ================= */}
        <div className="col-span-1 bg-white rounded-xl border border-neutral-200 shadow p-4 overflow-y-auto max-h-[85vh]">
          <h2 className="font-semibold mb-4">Active Chats</h2>

          {sessions.length === 0 ? (
            <p className="text-sm text-neutral-500">No active sessions.</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => openSession(s)}
                  className={`w-full text-left px-4 py-3 rounded-xl border ${
                    activeSession?.email === s.email
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white hover:bg-neutral-100 border-neutral-200 text-neutral-800"
                  }`}
                >
                  <p className="font-semibold text-sm">{s.name || "User"}</p>
                  <p className="text-xs opacity-80">{s.email}</p>
                  <p className="text-[11px] text-neutral-400 mt-1">
                    Started:{" "}
                    {s.startedAt
                      ? new Date(s.startedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "--"}
                  </p>
                </button>
              ))}
            </div>
          )}

          {activeSession && (
            <div className="mt-6 border-t pt-4 text-sm text-neutral-700 space-y-1">
              <h3 className="font-semibold mb-2 text-neutral-900">
                Customer Details
              </h3>
              <p><b>Name:</b> {activeSession.name || "N/A"}</p>
              <p><b>Email:</b> {activeSession.email}</p>
              <p><b>Phone:</b> {activeSession.phone || "N/A"}</p>
              <p><b>City:</b> {activeSession.city || "N/A"}</p>
              <p><b>Province:</b> {activeSession.province || "N/A"}</p>
              <p><b>Postal Code:</b> {activeSession.postalCode || "N/A"}</p>
            </div>
          )}
        </div>

        {/* ================= RIGHT: Chat Window ================= */}
        <div className="col-span-3 bg-white rounded-xl border border-neutral-200 shadow p-5 flex flex-col">
          {activeSession ? (
            <>
              <div className="flex justify-between items-center border-b pb-3 mb-3">
                <h2 className="text-lg font-semibold">
                  Chat with {activeSession.email}
                </h2>
                <button
                  onClick={() => endChat(activeSession.email)}
                  className="text-sm text-red-600 hover:underline"
                >
                  End Chat
                </button>
              </div>

              <div className="flex-1 overflow-y-auto mb-4 p-3 bg-neutral-50 rounded-xl space-y-3">
                {loading ? (
                  <p className="text-center text-neutral-500">Loading chat...</p>
                ) : messages.length === 0 ? (
                  <p className="text-center text-neutral-500 mt-10 text-sm">
                    No messages yet.
                  </p>
                ) : (
                  messages.map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${
                        m.sender === "Admin" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`px-4 py-2 rounded-2xl shadow-sm text-sm max-w-[70%] ${
                          m.sender === "Admin"
                            ? "bg-neutral-900 text-white"
                            : "bg-neutral-200 text-neutral-900"
                        }`}
                      >
                        <p>{m.text || m.content}</p>
                        <p className="text-[10px] opacity-70 mt-1 text-right">
                          {new Date(m.timestamp || m.time).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={sendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 border border-neutral-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-neutral-900 focus:outline-none"
                />
                <button
                  type="submit"
                  className="bg-neutral-900 text-white px-5 rounded-xl hover:opacity-90 transition"
                >
                  Send
                </button>
              </form>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 text-neutral-500">
              Select a chat on the left to view conversation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportChat;
